// @ts-check

import { createLog } from '../utils/logger'
import { Broker } from '../utils/broker'
import createUserPreference from './user-preference'
import displayPurposes from './display-purposes'
import pick from 'lodash.pick'

const log = createLog()

const uiNamespace = '@mol-fe/mol-fe-cmp'

function loadScript (uiScriptBase, appState) {
  log('loadScript', appState)
  const script = (src) => {
    const script = document.createElement('script')
    script.dataset.state = JSON.stringify(appState)
    script.src = src
    script.id = 'mol-fe-cmp-script'
    return script
  }

  const src = uiScriptBase
  document.body.appendChild(script(src))
}

function uiHandlers (broker, clientBroker, cmpUpdateHandlers, removeStorageListener) {
  const handler = h => (...args) => {
    if (removeStorageListener) {
      removeStorageListener()
      removeStorageListener = null
    }
    h(...args)
  }

  broker.on('close', handler(data => { cmpUpdateHandlers.updateConsent(data) }))
  broker.on('close:allow_all', handler(() => { cmpUpdateHandlers.consentToAll() }))
  broker.on('close:deny_all', handler(() => { cmpUpdateHandlers.dissentToAll() }))
  broker.on('level_changed', data => {
    log('level_changed', data)
    cmpUpdateHandlers.levelChanged(data)
    clientBroker.emit('level_changed', data)
  })
}

function removeVendors (vendors) {
  const consentableVendors = Object.values(vendors).filter(vendor => {
    const consentable = vendor.purposes.length !== 0 || vendor.legIntPurposes.length !== 0 || vendor.specialPurposes.length !== 0
    !consentable && log('removing', vendor.id, vendor.name)
    return consentable
  })
  return consentableVendors.reduce((acc, vendor) => {
    acc[vendor.id] = vendor
    return acc
  }, {})
}

function handleExternalPreferenceUpdate (broker, reloadTcModel) {
  const handler = ({ key, newValue }) => {
    if (key !== 'mol.ads.cmp.tcf.client') return
    remove()
    const tcString = newValue
    try {
      reloadTcModel(tcString)
      broker.emit('close')
    } catch (error) {
      console.error(error)
    }
  }

  window.addEventListener('storage', handler)

  const remove = () => {
    window.removeEventListener('storage', handler)
  }
  return remove
}

export default class Ui {
  constructor ({ brand, uiScriptUrl, renderPlatform, uiLevel, clientBroker }) {
    this.scriptLoaded = false
    this.brand = brand
    this.uiScriptUrl = uiScriptUrl
    this.renderPlatform = renderPlatform
    this.uiLevel = uiLevel
    this.clientBroker = clientBroker
    this.brokerId = Math.random().toString(36).substring(2)
    this.broker = new Broker(uiNamespace, '@mol-ads/mol-ads-tcf', this.brokerId)
    this.open = this.open.bind(this)
  }

  /**
   * @param {UiLevel} currentLevel 
   * @param {CmpModelForUi} cmpModel 
   * @returns 
   */
  createModel (currentLevel, cmpModel) {
    const gvl = cmpModel.tcModel.gvl
    const { purposes, specialPurposes, features, specialFeatures } = gvl
    const stacks = pick(gvl.stacks, [42])
    const vendors = removeVendors(gvl.vendors)
    const userPrefs = createUserPreference(cmpModel)
    const nonTcfVendors = cmpModel.acmVendors.cvl
    const nonRegisteredVendors = cmpModel.nonRegisteredVendors.cvl
    return {
      config: {
        hash: {
          purposes,
          specialPurposes,
          features,
          specialFeatures,
          vendors,
          stacks,
          nonTcfVendors,
          nonRegisteredVendors
        },
        userPrefs,
        displayPurposes
      },
      currentLevel: currentLevel || this.uiLevel
    }
  }

  /**
   * @param {*} uiLevel 
   * @param {{cmpModel: CmpModelForUi, cmpUpdateHandlers: CmpUpdateHandlers}} opts
   */
  open (uiLevel, { cmpModel, cmpUpdateHandlers }) {
    const model = this.createModel(uiLevel, cmpModel)
    log('open', model)
    if (!this.scriptLoaded) {
      const removeStorageListener = handleExternalPreferenceUpdate(this.broker, cmpUpdateHandlers.reloadTcModel)
      uiHandlers(this.broker, this.clientBroker, cmpUpdateHandlers, removeStorageListener)
      loadScript(this.uiScriptUrl, {
        ...model,
        renderPlatform: this.renderPlatform,
        brand: this.brand,
        id: this.brokerId
      })
      this.scriptLoaded = true
    } else {
      this.broker.emit('open', model)
    }
  }

  notifyUserPreferenceCaptured () {
    log('publishing user_consent_captured')
    this.broker.emit('user_consent_captured')
  }
}

/**
 * @typedef {Object} CmpModelForUi
 * @property {import('@mol-ads/tcf-core').TCModel} tcModel
 * @property {boolean} hasUserSelectedTheirPreference
 * @property {import('../cmp/NonIABModel').UiModel} acmVendors
 * @property {import('../cmp/NonIABModel').UiModel} nonRegisteredVendors
 */

/**
 * @typedef {Record<string,()=>void>} CmpUpdateHandlers
 */

/**
 * @typedef {'Level1'|'Level2'} UiLevel
 */

